import { useEffect, useId, type ReactNode } from 'react';

export interface ModalProps {
  title: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
  /** Size modifier. */
  size?: 'sm' | 'lg' | 'xl';
  /** Vertically center the dialog. */
  centered?: boolean;
  // --- Controlled mode (React-managed, no Bootstrap JS) ---
  /** When provided, the modal is controlled: render it only while `open` is true. */
  open?: boolean;
  /** Called when the user dismisses (×, backdrop, or Escape). */
  onClose?: () => void;
  // --- Uncontrolled mode (Bootstrap data API) ---
  /** Trigger button label. Omit to render only the modal markup (control by id). */
  triggerLabel?: ReactNode;
  triggerClassName?: string;
  /** Provide a fixed id (otherwise auto-generated). */
  id?: string;
}

/** Bootstrap 5 modal — controlled via `open`/`onClose`, or uncontrolled via a trigger. */
export function Modal({
  title,
  children,
  footer,
  size,
  centered = false,
  open,
  onClose,
  triggerLabel,
  triggerClassName = 'btn btn-primary',
  id,
}: ModalProps) {
  const autoId = `modal-${useId().replace(/:/g, '')}`;
  const modalId = id ?? autoId;
  const controlled = open !== undefined;

  const dialogClass = ['modal-dialog', size ? `modal-${size}` : '', centered ? 'modal-dialog-centered' : '']
    .filter(Boolean)
    .join(' ');

  // Controlled mode: lock body scroll + close on Escape while open.
  useEffect(() => {
    if (!controlled || !open) return;
    document.body.classList.add('modal-open');
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.body.classList.remove('modal-open');
      document.removeEventListener('keydown', onKey);
    };
  }, [controlled, open, onClose]);

  const content = (
    <div className="modal-content">
      <div className="modal-header">
        <h5 className="modal-title">{title}</h5>
        <button
          type="button"
          className="btn-close"
          aria-label="Close"
          {...(controlled ? { onClick: onClose } : { 'data-bs-dismiss': 'modal' })}
        />
      </div>
      <div className="modal-body">{children}</div>
      {footer && <div className="modal-footer">{footer}</div>}
    </div>
  );

  if (controlled) {
    if (!open) return null;
    return (
      <>
        <div className="modal-backdrop fade show" />
        <div
          className="modal fade show d-block"
          tabIndex={-1}
          role="dialog"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) onClose?.();
          }}
        >
          <div className={dialogClass} role="document">
            {content}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      {triggerLabel && (
        <button type="button" className={triggerClassName} data-bs-toggle="modal" data-bs-target={`#${modalId}`}>
          {triggerLabel}
        </button>
      )}
      <div className="modal fade" id={modalId} tabIndex={-1} aria-hidden="true">
        <div className={dialogClass}>{content}</div>
      </div>
    </>
  );
}
